import api from "@/api";

import deepClone from "@/utils/deep-clone";

function generateDefaultAutomation({ template }) {
    return {
        name: "",
        template,
        status: "active",
        timing: {
            mode: "once",
            repeatsEveryWeeks: 12,
        },
        inclusionTrigger: {
            mode: "all",
            specificTeams: [],
            weeksAfterStart: 12,
        },
    };
}

const state = {
    list: [],
    details: {},
    preview: {},
};

const getters = {
    list(state) {
        return deepClone(state.list || []);
    },
    details(state) {
        return id => deepClone(state.details[id] || {});
    },
    preview(state) {
        return id => state.preview[id] || [];
    },
};

const actions = {
    getList({ commit }) {
        return api.get(`/api/reviewcycles/get/automations/list`).then(res => {
            const list = res.data;
            commit("updateList", { list });
        });
    },

    getDetails({ commit, state }, { payload: { id } }) {
        return api
            .get(`/api/reviewcycles/get/automations/details/${id}`)
            .then(res => {
                const automation = res.data;
                commit("updateDetails", { automation });
            })
            .catch(err => {
                // As we are performing upserts, we ignore 404 errors if the local state holds some data for
                // a corresponding id.
                if (
                    err?.response?.status === 404 &&
                    Object.keys(getters.details(state)(id)).length
                ) {
                    return;
                }

                throw err;
            });
    },
    getPreview({ commit }, { payload: { id } }) {
        return api
            .get(`/api/reviewcycles/get/automations/details/${id}/preview`)
            .then(res => {
                const participants = res.data;
                commit("updatePreview", { id, participants });
            })
            .catch(err => {
                // As we are performing upserts, we ignore 404 errors if the local state holds some data for
                // a corresponding id.
                if (
                    err?.response?.status === 404 &&
                    Object.keys(getters.details(state)(id)).length
                ) {
                    return;
                }

                throw err;
            });
    },

    create({ commit, rootGetters }, { payload: { automation = {} } = {} }) {
        // Get list of templates
        const templates = rootGetters["reviews/assessments/templates"];

        if (!templates.length) {
            throw new Error();
        }

        // Generate new goal based on defaults
        const newAutomation = {
            ...generateDefaultAutomation({ template: templates[0]._id }),
            ...automation,
        };

        commit("updateDetails", { automation: newAutomation });
    },
    update({ commit }, { payload: { automation } }) {
        return api.post(`/api/reviewcycles/create/automation`, automation).then(res => {
            if (!res.data) {
                return;
            }

            const updatedAutomation = res.data;
            commit("updateDetails", { automation: updatedAutomation });
        });
    },
    delete({ commit, state }, { payload: { id } }) {
        return api.post(`/api/reviewcycles/remove/automation`, { _id: id }).then(() => {
            commit("deleteDetails", { id });
            commit("updateList", { list: state.list.filter(automation => automation._id !== id) });
        });
    },
};

const mutations = {
    updateList(state, { list }) {
        state.list = [...list];
    },
    updateDetails(state, { automation }) {
        state.details[automation._id] = { ...automation };
    },
    updatePreview(state, { id, participants }) {
        state.preview[id] = [...participants];
    },
    deleteDetails(state, { id }) {
        delete state.details[id];
        delete state.preview[id];
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
